// Dynamic Diesels — Slide-in cart drawer.
// - Intercepts product-form submits and [data-quick-add-id] clicks → AJAX add.
// - Re-renders the drawer via the Section Rendering API (no page reload).
// - Handles qty change / remove inside the drawer.
// - Opens from the header/tab-bar cart buttons.

(function () {
  'use strict';

  const SECTION = 'dd-cart-drawer';

  function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function readPrebuyFromRoot(root) {
    if (!root) return null;
    const vin = (root.querySelector('[data-prebuy-vin]')?.value || '').trim();
    const width = (root.querySelector('[data-prebuy-tire-width]')?.value || '').trim();
    const aspect = (root.querySelector('[data-prebuy-tire-aspect]')?.value || '').trim();
    const rim = (root.querySelector('[data-prebuy-tire-rim]')?.value || '').trim();
    return {
      title: (root.getAttribute('data-prebuy-title') || '').trim(),
      vin,
      width,
      aspect,
      rim,
    };
  }

  function readPrebuyFromForm(form) {
    return form ? readPrebuyFromRoot(form.querySelector('[data-prebuy-fields]')) : null;
  }

  function hasPrebuyValues(prebuy) {
    return Boolean(prebuy && (prebuy.vin || prebuy.width || prebuy.aspect || prebuy.rim));
  }

  function formatTireSize(prebuy) {
    const width = prebuy.width || '';
    const aspect = prebuy.aspect || '';
    const rim = prebuy.rim ? 'R' + prebuy.rim : '';
    return [width, aspect, rim].filter(Boolean).join(' / ');
  }

  function buildVehicleBlock(prebuy) {
    if (!hasPrebuyValues(prebuy)) return '';
    const lines = ['---'];
    if (prebuy.title) lines.push('Product: ' + prebuy.title);
    if (prebuy.vin) lines.push('VIN: ' + prebuy.vin);
    if (prebuy.width || prebuy.aspect || prebuy.rim) {
      lines.push('Tire size: ' + formatTireSize(prebuy));
    }
    lines.push('---');
    return lines.join('\n');
  }

  function mergeVehicleNote(existing, prebuy, block) {
    let next = String(existing || '');
    const title = prebuy && prebuy.title;

    // If this product already has a block, replace only that block so VIN edits
    // do not duplicate. Everything else in the note is left intact.
    if (title) {
      next = next.replace(
        new RegExp(
          '^---\\s*\\nProduct: ' + escapeRegExp(title) + '\\n(?:VIN:.*\\n)?(?:Tire size:.*\\n)?---\\s*$',
          'gm'
        ),
        ''
      );
      next = next.replace(
        new RegExp(
          '^--- Vehicle info \\(' + escapeRegExp(title) + '\\) ---\\n[\\s\\S]*?^---\\s*$',
          'gm'
        ),
        ''
      );
    }
    if (prebuy && prebuy.vin) {
      next = next.replace(
        new RegExp(
          '^---\\s*\\nVIN: ' + escapeRegExp(prebuy.vin) + '\\n(?:Tire size:.*\\n)?---\\s*$',
          'gm'
        ),
        ''
      );
    }

    next = next.replace(/\n{3,}/g, '\n\n').trim();
    if (!block) return next;
    return next ? next + '\n\n' + block : block;
  }

  function readModsFromForm(form) {
    const tune = form && form.querySelector('[data-mp-custom-tune]');
    if (!tune || !tune.checked) return '';
    return (form.querySelector('[data-mp-custom-tune-mods]')?.value || '').trim();
  }

  function readNoteTitle(form, prebuy) {
    if (prebuy && prebuy.title) return prebuy.title;
    const block = form && form.querySelector('[data-mp-custom-tune-block]');
    return (block && block.getAttribute('data-product-title') || '').trim();
  }

  function buildModsBlock(mods, title) {
    if (!mods) return '';
    const lines = ['---'];
    lines.push(title ? 'Mods (' + title + '):' : 'Mods:');
    lines.push(mods);
    lines.push('---');
    return lines.join('\n');
  }

  function mergeModsNote(existing, title, block) {
    let next = String(existing || '');
    if (title) {
      next = next.replace(
        new RegExp(
          '^---\\s*\\nMods \\(' + escapeRegExp(title) + '\\):\\n[\\s\\S]*?^---\\s*$',
          'gm'
        ),
        ''
      );
    } else {
      next = next.replace(/^---\s*\nMods:\n[\s\S]*?^---\s*$/gm, '');
    }
    next = next.replace(/\n{3,}/g, '\n\n').trim();
    if (!block) return next;
    return next ? next + '\n\n' + block : block;
  }

  async function syncOrderNotes(prebuy, form) {
    const mods = readModsFromForm(form);
    const title = readNoteTitle(form, prebuy);
    const vehicleBlock = hasPrebuyValues(prebuy) ? buildVehicleBlock(prebuy) : '';
    const modsBlock = buildModsBlock(mods, title);
    if (!vehicleBlock && !modsBlock) return;
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!cartRes.ok) return;
      const cart = await cartRes.json();
      let note = String(cart.note || '');
      if (vehicleBlock) note = mergeVehicleNote(note, prebuy, vehicleBlock);
      if (modsBlock) note = mergeModsNote(note, title, modsBlock);
      if (note === String(cart.note || '').trim()) return;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    } catch (e) { /* no-op */ }
  }

  function stripEmptyProperties(fd) {
    const keys = [];
    for (const key of fd.keys()) {
      if (key.indexOf('properties[') === 0) keys.push(key);
    }
    Array.from(new Set(keys)).forEach((key) => {
      if (!String(fd.get(key) || '').trim()) fd.delete(key);
    });
  }

  function bindNoteFieldSync() {
    let timer;
    function schedule(form) {
      if (!form) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        syncOrderNotes(readPrebuyFromForm(form), form);
      }, 500);
    }
    document.addEventListener('input', (e) => {
      const prebuy = e.target.closest && e.target.closest('[data-prebuy-fields]');
      const mods = e.target.closest && e.target.closest('[data-mp-custom-tune-mods]');
      if (prebuy || mods) schedule(e.target.closest('form'));
    });
    document.addEventListener('change', (e) => {
      const prebuy = e.target.closest && e.target.closest('[data-prebuy-fields]');
      const tune = e.target.closest && e.target.closest('[data-mp-custom-tune-block]');
      if (prebuy || tune) schedule(e.target.closest('form'));
    });
  }

  function init() {
    bindNoteFieldSync();

    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    const liveInner = drawer.querySelector('[data-cart-inner]');
    const liveTitle = drawer.querySelector('.dd-cart__title');

    /* ----------------------------  open / close  --------------------------- */

    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('dd-cart-open');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('dd-cart-open');
      document.body.style.overflow = '';
    }
    window.ddCart = { open, close, refresh };

    // Cart buttons across the site open the drawer instead of navigating.
    document.querySelectorAll('[data-cart-open]').forEach((el) => {
      el.addEventListener('click', (e) => { e.preventDefault(); open(); });
    });
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]') || !e.target.closest('.dd-cart__panel')) {
        close();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    /* ----------------------------  helpers  -------------------------------- */

    function setBusy(on) { drawer.classList.toggle('is-loading', on); }

    function updateFromHtml(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newInner = doc.querySelector('[data-cart-inner]');
      const newTitle = doc.querySelector('.dd-cart__title');
      if (newInner && liveInner) liveInner.innerHTML = newInner.innerHTML;
      if (newTitle && liveTitle) liveTitle.innerHTML = newTitle.innerHTML;
      // Sync header/tab-bar count bubbles.
      const countEl = doc.querySelector('.dd-cart__count');
      const count = countEl ? countEl.textContent.replace(/[()]/g, '').trim() : '';
      document.querySelectorAll('[data-cart-bubble]').forEach((b) => {
        b.textContent = count ? '(' + count + ')' : '';
      });
    }

    async function refresh() {
      try {
        const res = await fetch('/?sections=' + SECTION, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        updateFromHtml(data[SECTION]);
      } catch (e) { /* no-op */ }
    }

    function formDataToCartItem(fd) {
      const item = {
        id: Number(fd.get('id')),
        quantity: parseInt(fd.get('quantity'), 10) || 1,
      };
      const properties = {};
      fd.forEach((value, key) => {
        const match = String(key).match(/^properties\[([^\]]+)\]$/);
        if (match && String(value || '').trim()) properties[match[1]] = String(value).trim();
      });
      if (Object.keys(properties).length) item.properties = properties;
      return item;
    }

    function readCheckedAddonId(form) {
      const tune = form && form.querySelector('[data-mp-custom-tune]');
      if (!tune || !tune.checked) return '';
      return String(tune.getAttribute('data-addon-id') || '').trim();
    }

    async function postCartAdd(payload) {
      const isForm = typeof FormData !== 'undefined' && payload instanceof FormData;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: isForm
          ? { Accept: 'application/javascript' }
          : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: isForm ? payload : JSON.stringify(payload),
      });
      const data = await res.json();
      return { res, data };
    }

    async function addToCart(body, prebuy, form) {
      setBusy(true);
      try {
        if (body instanceof FormData) body.append('sections', SECTION);
        else body.sections = SECTION;

        let { res, data } = await postCartAdd(body);
        // If the bundled add-on request fails, still add the main product.
        if (!res.ok && body && Array.isArray(body.items) && body.items.length > 1) {
          console.warn('Add to cart with add-on failed, retrying main item only', data);
          const fallback = { items: [body.items[0]], sections: SECTION };
          ({ res, data } = await postCartAdd(fallback));
        }
        if (res.ok) {
          if (data.sections && data.sections[SECTION]) {
            updateFromHtml(data.sections[SECTION]);
          } else {
            await refresh();
          }
          open();
          await syncOrderNotes(prebuy, form);
        } else {
          // Out of stock / other error — fall back to the product page form.
          console.warn('Add to cart failed', data);
        }
      } catch (e) { /* no-op */ }
      setBusy(false);
    }

    async function changeLine(line, quantity) {
      setBusy(true);
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: line, quantity: quantity, sections: SECTION }),
        });
        const data = await res.json();
        updateFromHtml(data.sections && data.sections[SECTION]);
      } catch (e) { /* no-op */ }
      setBusy(false);
    }

    /* --------------------------  add-to-cart hooks  ------------------------ */

    // 1) Any product form submit (main PDP form, etc.)
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
      e.preventDefault();
      const fd = new FormData(form);
      stripEmptyProperties(fd);
      const addonId = readCheckedAddonId(form);
      if (addonId) {
        const main = formDataToCartItem(fd);
        const items = [main];
        if (Number(addonId) !== main.id) {
          items.push({ id: Number(addonId), quantity: main.quantity });
        }
        addToCart({ items }, readPrebuyFromForm(form), form);
        return;
      }
      addToCart(fd, readPrebuyFromForm(form), form);
    });

    // 2) Quick-add buttons on recommendation cards.
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-add-id]');
      if (!btn) return;
      e.preventDefault();
      const body = new FormData();
      body.append('id', btn.getAttribute('data-quick-add-id'));
      body.append('quantity', '1');
      addToCart(body);
    });

    /* --------------------------  in-drawer qty/remove  --------------------- */

    drawer.addEventListener('click', (e) => {
      const item = e.target.closest('[data-cart-item]');
      if (!item) return;
      const line = parseInt(item.getAttribute('data-line'), 10);
      const input = item.querySelector('[data-cart-qty]');
      const current = parseInt(input ? input.value : '1', 10) || 1;

      if (e.target.closest('[data-cart-remove]')) { changeLine(line, 0); return; }
      if (e.target.closest('[data-cart-plus]')) { changeLine(line, current + 1); return; }
      if (e.target.closest('[data-cart-minus]')) { changeLine(line, Math.max(0, current - 1)); return; }
    });

    drawer.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      const item = input.closest('[data-cart-item]');
      const line = parseInt(item.getAttribute('data-line'), 10);
      const q = Math.max(0, parseInt(input.value, 10) || 0);
      changeLine(line, q);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
